/**
 * Haptic Feedback Utilities
 *
 * Provides tactile feedback for touch interactions on supported devices.
 * Uses the Vibration API for Android and Haptic Feedback for iOS Safari.
 *
 * Browser Support:
 * - iOS Safari 13+: UIImpactFeedbackGenerator via webkit
 * - Android Chrome/Firefox: Vibration API
 * - Desktop: No-op (gracefully degrades)
 */

/**
 * Haptic feedback intensity levels
 */
export type HapticIntensity = "light" | "medium" | "heavy";

/**
 * Haptic feedback types
 */
export type HapticType = "impact" | "notification" | "selection";

/**
 * Check if haptic feedback is supported
 */
export const isHapticsSupported = (): boolean => {
    if (typeof window === "undefined") {
        return false;
    }

    // Check for iOS Haptic Engine (via webkit)
    if ("ontouchstart" in window && navigator.userAgent.match(/iPhone|iPad|iPod/)) {
        return true;
    }

    // Check for Vibration API (Android)
    return "vibrate" in navigator;
};

/**
 * Trigger haptic feedback
 *
 * @param type Type of haptic feedback
 * @param intensity Intensity level (only affects Vibration API)
 *
 * @example
 * ```tsx
 * import { triggerHaptic } from './utils/haptics';
 *
 * <button onClick={() => {
 *   triggerHaptic('impact', 'medium');
 *   // ... handle click
 * }}>
 *   Click me
 * </button>
 * ```
 */
export const triggerHaptic = (type: HapticType = "impact", intensity: HapticIntensity = "medium"): void => {
    if (!isHapticsSupported()) {
        return;
    }

    // iOS Haptic Engine via webkit (no public API, but works via Vibration API in Safari)
    // Note: iOS doesn't expose the Haptic Engine directly to web, but respects
    // the Vibration API in certain contexts

    // Vibration API (Android and some iOS contexts)
    if ("vibrate" in navigator) {
        const patterns: Record<HapticType, Record<HapticIntensity, number | number[]>> = {
            impact: {
                light: 10,
                medium: 20,
                heavy: 30,
            },
            notification: {
                light: [10, 50, 10],
                medium: [20, 50, 20],
                heavy: [30, 50, 30],
            },
            selection: {
                light: 5,
                medium: 10,
                heavy: 15,
            },
        };

        const pattern = patterns[type][intensity];

        if (typeof pattern === "number") {
            navigator.vibrate(pattern);
        } else {
            navigator.vibrate(pattern);
        }
    }
};

/**
 * Trigger light impact haptic (subtle feedback)
 *
 * Use for: Button presses, checkbox toggles, light interactions
 *
 * @example
 * ```tsx
 * <button onClick={() => {
 *   impactLight();
 *   toggle();
 * }}>Toggle</button>
 * ```
 */
export const impactLight = (): void => {
    triggerHaptic("impact", "light");
};

/**
 * Trigger medium impact haptic (standard feedback)
 *
 * Use for: Primary actions, form submissions, navigation
 *
 * @example
 * ```tsx
 * <button onClick={() => {
 *   impactMedium();
 *   submitForm();
 * }}>Submit</button>
 * ```
 */
export const impactMedium = (): void => {
    triggerHaptic("impact", "medium");
};

/**
 * Trigger heavy impact haptic (strong feedback)
 *
 * Use for: Important actions, errors, warnings, destructive actions
 *
 * @example
 * ```tsx
 * <button onClick={() => {
 *   impactHeavy();
 *   deleteItem();
 * }}>Delete</button>
 * ```
 */
export const impactHeavy = (): void => {
    triggerHaptic("impact", "heavy");
};

/**
 * Trigger selection haptic (lightweight feedback)
 *
 * Use for: Carousel navigation, slider changes, tab switches
 *
 * @example
 * ```tsx
 * const handleSwipe = () => {
 *   selectionHaptic();
 *   goToNextSlide();
 * };
 * ```
 */
export const selectionHaptic = (): void => {
    triggerHaptic("selection", "light");
};

/**
 * Trigger notification haptic (success feedback)
 *
 * Use for: Success messages, completion notifications
 *
 * @example
 * ```tsx
 * const handleSuccess = () => {
 *   notificationSuccess();
 *   showToast('Saved!');
 * };
 * ```
 */
export const notificationSuccess = (): void => {
    triggerHaptic("notification", "medium");
};

/**
 * Trigger notification haptic (error feedback)
 *
 * Use for: Error messages, validation failures
 *
 * @example
 * ```tsx
 * const handleError = () => {
 *   notificationError();
 *   showToast('Error!');
 * };
 * ```
 */
export const notificationError = (): void => {
    triggerHaptic("notification", "heavy");
};
